import { translateKey } from 'src/locales/translate';
// form
import { Controller, useFormContext } from 'react-hook-form';
// @mui
import {
  Box,
  Checkbox,
  Chip,
  FormControl,
  FormHelperText,
  InputLabel,
  ListSubheader,
  MenuItem,
  OutlinedInput,
  Select,
  SelectProps,
  TextField,
  TextFieldProps,
  Typography,
} from '@mui/material';
import { useMemo } from 'react';

// ----------------------------------------------------------------------

type RHFSelectProps = TextFieldProps & {
  name: string;
  native?: boolean;
  maxHeight?: boolean | number;
  children: React.ReactNode;
};

export function RHFSelect({
  name,
  native,
  maxHeight = 220,
  helperText,
  children,
  ...other
}: RHFSelectProps) {
  const { control } = useFormContext();

  const selectProps = useMemo(
    () => ({
      native,
      MenuProps: {
        PaperProps: {
          sx: {
            ...(!native && {
              px: 1,
              maxHeight: typeof maxHeight === 'number' ? maxHeight : 'unset',
              '& .MuiMenuItem-root': {
                px: 1,
                borderRadius: 0.75,
                typography: 'body2',
                textTransform: 'capitalize',
              },
            }),
          },
        },
      },
      sx: { textTransform: 'capitalize' },
    }),
    [native, maxHeight],
  );

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <TextField
          {...field}
          select
          fullWidth
          SelectProps={selectProps}
          error={!!error}
          helperText={error ? error?.message : helperText}
          {...other}
        >
          {children}
        </TextField>
      )}
    />
  );
}

// ----------------------------------------------------------------------

type RHFMultiSelectProps = SelectProps & {
  name: string;
  label?: string;
  chip?: boolean;
  checkbox?: boolean;
  placeholder?: string;
  helperText?: React.ReactNode;
  options: {
    label: string;
    value: string;
    group?: string;
  }[];
};

export function RHFMultiSelect({
  name,
  chip,
  label,
  options,
  checkbox,
  placeholder,
  helperText,
  sx,
  ...other
}: RHFMultiSelectProps) {
  const { control } = useFormContext();
  const groupedOptions = useMemo(() => {
    const hasGroups = options.some((opt) => opt.group);
    if (!hasGroups) return { ungrouped: options };

    return options.reduce((acc, option) => {
      const groupName = option.group || translateKey("search.specializationOther");
      if (!acc[groupName]) acc[groupName] = [];
      acc[groupName].push(option);
      return acc;
    }, {} as Record<string, typeof options>);
  }, [options]);

  const renderValues = (selected: unknown) => {
    const selectedIds = selected as string[];
    const selectedItems = options.filter((item) =>
      selectedIds.includes(item.value),
    );

    if (!selectedItems.length && placeholder) {
      return (
        <Box component="em" sx={{ color: 'text.disabled' }}>
          {placeholder}
        </Box>
      );
    }

    if (chip) {
      return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {selectedItems.map((item) => (
            <Chip key={item.value} size="small" label={item.label} />
          ))}
        </Box>
      );
    }

    return selectedItems.map((item) => item.label).join(', ');
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <FormControl
          sx={[
            {
              '& .MuiChip-label': {
                whiteSpace: 'normal',
                wordBreak: 'break-word',
              },
              '& .MuiChip-root': {
                height: 'auto',
                padding: '4px',
              },
            },
            ...(Array.isArray(sx) ? sx : [sx]),
          ]}
        >
          {label && (
            <InputLabel
              error={!!error}
              required={other.required}
              id={name}
              sx={{ '&.Mui-focused': { color: 'text.primary' } }}
            >
              {label}
            </InputLabel>
          )}

          <Select
            {...field}
            multiple
            displayEmpty={!!placeholder}
            labelId={name}
            input={<OutlinedInput fullWidth label={label} error={!!error} />}
            renderValue={renderValues}
            MenuProps={{
              PaperProps: {
                sx: { px: 1, maxHeight: 280 },
              },
              MenuListProps: {
                sx: { display: 'flex', flexDirection: 'column', gap: 1 },
              },
            }}
            {...other}
          >
            {placeholder && (
              <MenuItem
                disabled
                value=""
                sx={{
                  py: 1,
                  px: 2,
                  borderRadius: 0.75,
                  typography: 'body2',
                }}
              >
                <em> {placeholder} </em>
              </MenuItem>
            )}

            {Object.entries(groupedOptions).map(
              ([groupName, groupOptions], index) => [
                groupName !== 'ungrouped' && (
                  <ListSubheader
                    key={`header-${groupName}`}
                    sx={{
                      lineHeight: '24px',
                      backgroundColor: '#F8F7F6',
                      ...(index > 0 && { borderTop: '1px solid #E0E0E0' }),
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontWeight: 'bold' }}
                    >
                      {groupName}
                    </Typography>
                  </ListSubheader>
                ),
                ...groupOptions.map((option) => {
                  const selected = field.value.includes(option.value);

                  return (
                    <MenuItem
                      key={option.value}
                      value={option.value}
                      sx={{
                        py: 1,
                        borderRadius: 0.75,
                        typography: 'body2',
                        fontSize: '13px',
                        ...(groupName !== 'ungrouped' && { pl: 2 }),
                        ...(selected && {
                          fontWeight: 'fontWeightMedium',
                        }),
                        ...(checkbox && {
                          p: 0.25,
                        }),
                      }}
                    >
                      {checkbox && (
                        <Checkbox
                          disableRipple
                          size="small"
                          checked={selected}
                        />
                      )}

                      {option.label}
                    </MenuItem>
                  );
                }),
              ],
            )}
          </Select>

          {(!!error || helperText) && (
            <FormHelperText error={!!error}>
              {error ? error?.message : helperText}
            </FormHelperText>
          )}
        </FormControl>
      )}
    />
  );
}
